import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Plus, X, Check, Tag, Eye, Printer, Download } from 'lucide-react';
import api from '../../api/client';
import { openPrintTab } from '../../utils/printHelper';
import { useAuth } from '../../context/AuthContext';

export default function DirectCodesPage() {
  const { can } = useAuth();
  const [batches, setBatches]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(false);
  const [detail, setDetail]         = useState(null);

  // Form state
  const [schools, setSchools]           = useState([]);
  const [batchList, setBatchList]       = useState([]);
  const [batchTuts, setBatchTuts]       = useState([]);
  const [academicYears, setAcademicYears] = useState([]);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm();
  const watchBatch = watch('batch_id');
  const watchQty   = watch('quantity', 0);
  const watchPrice = watch('base_price', 0);

  useEffect(() => {
    if (watchBatch) {
      api.get(`/batches/${watchBatch}/tutorials`).then(r => setBatchTuts(r.data.data)).catch(() => setBatchTuts([]));
    } else setBatchTuts([]);
  }, [watchBatch]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/direct-codes');
      setBatches(data.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    reset({ school_id:'', batch_id:'', tutorial_id:'', quantity:1, base_price:0, notes:'' });
    try {
      const [sc, ba, ay] = await Promise.all([
        api.get('/schools'),
        api.get('/batches'),
        api.get('/masters/academic-years'),
      ]);
      setSchools(sc.data.data);
      setBatchList(ba.data.data.filter(b => !b.disable));
      setAcademicYears(ay.data.data);
    } catch { toast.error('Failed to load data'); return; }
    setModal(true);
  };

  const onSubmit = async (values) => {
    try {
      const { data } = await api.post('/direct-codes', { ...values, quantity: Number(values.quantity), base_price: Number(values.base_price) });
      toast.success(data.message);
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const openDetail = async (batch) => {
    try {
      const { data } = await api.get(`/direct-codes/${batch.id}`);
      setDetail(data.data);
    } catch { toast.error('Failed to load'); }
  };

  const openPrint = (id) => openPrintTab(`direct/${id}`);

  const downloadCSV = async (id, ref) => {
    try {
      const { data } = await api.get(`/direct-codes/${id}/codes?limit=10000`);
      const rows = ['Code,Vol ID,Print Code,Base Price,Activated,Disabled'];
      data.data.forEach(c => rows.push(`${c.code},${c.vol_id},${c.print_code},${c.base_price},${c.stdOne},${c.disable}`));
      const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `${ref}_codes.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const totalValue = (Number(watchQty) || 0) * (Number(watchPrice) || 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Direct Code Creation</h1>
          <p className="page-subtitle">Business Admin — create student codes directly with base price, no order flow</p>
        </div>
        {can('direct_codes','create') && (
          <button className="btn btn-primary" onClick={openCreate}><Plus size={15} /> Create Codes</button>
        )}
      </div>

      <div className="alert alert-info" style={{ marginBottom: 20, fontSize: 13 }}>
        <Tag size={16} />
        <span>
          <strong>Direct Code Creation</strong> bypasses the sales quote/approval flow.
          Use this when you want to generate codes immediately for a school and set the base price per book.
          These codes are printed on book stickers (each code usable by 2 users: student + parent).
        </span>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title"><Tag size={15} style={{ color: 'var(--orange-400)' }} /> {batches.length} code batches created</span>
        </div>
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader" /></div> : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Batch Ref</th>
                  <th>School</th>
                  <th>Batch / Tutorial</th>
                  <th>Codes</th>
                  <th>Price/Code</th>
                  <th>Total Value</th>
                  <th>Print Code</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.id}>
                    <td><code style={{ fontSize: 11, background: 'var(--grey-100)', padding: '2px 7px', borderRadius: 4 }}>{b.batch_ref}</code></td>
                    <td style={{ fontWeight: 500, fontSize: 13 }}>{b.school_name}</td>
                    <td>
                      <div style={{ fontSize: 13 }}>{b.batchName}</div>
                      <div style={{ fontSize: 11, color: 'var(--grey-400)' }}>{b.tutorial_name}</div>
                    </td>
                    <td><span className="badge badge-blue">{b.codes_created}</span></td>
                    <td style={{ fontWeight: 600, color: 'var(--orange-600)' }}>
                      {Number(b.base_price) > 0 ? `₹${Number(b.base_price).toFixed(2)}` : <span style={{ color: 'var(--grey-300)' }}>—</span>}
                    </td>
                    <td style={{ fontWeight: 700 }}>
                      {Number(b.total_value) > 0 ? `₹${Number(b.total_value).toFixed(2)}` : <span style={{ color: 'var(--grey-300)' }}>—</span>}
                    </td>
                    <td><code style={{ fontSize: 11, color: 'var(--orange-600)' }}>{b.print_code}</code></td>
                    <td style={{ fontSize: 11, color: 'var(--grey-400)' }}>{new Date(b.created_at).toLocaleDateString('en-IN')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn-icon" title="View codes" onClick={() => openDetail(b)}><Eye size={14} /></button>
                        <button className="btn-icon" title="Print QR codes" onClick={() => openPrint(b.id)}><Printer size={14} /></button>
                        <button className="btn-icon" title="Download CSV" onClick={() => downloadCSV(b.id, b.batch_ref)}><Download size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!batches.length && (
                  <tr><td colSpan={9}>
                    <div className="empty-state"><Tag size={36} /><h3>No direct code batches yet</h3></div>
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ─── Detail Modal ────────────────────────── */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title"><Tag size={18} /> {detail.batch_ref}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-sm btn-secondary" onClick={() => openPrint(detail.id)}><Printer size={12} /> Print QR</button>
                <button className="btn-icon" onClick={() => setDetail(null)}><X size={18} /></button>
              </div>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
                {[
                  { l: 'School',    v: detail.school_name },
                  { l: 'Batch',     v: detail.batchName },
                  { l: 'Tutorial',  v: `${detail.tutorial_name} [${detail.tutorial_key}]` },
                  { l: 'Codes',     v: detail.codes_created },
                  { l: 'Price/Code',v: `₹${Number(detail.base_price).toFixed(2)}` },
                  { l: 'Print Code',v: detail.print_code },
                ].map(x => (
                  <div key={x.l} style={{ background: 'var(--orange-50)', borderRadius: 6, padding: '8px 12px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--orange-600)', textTransform: 'uppercase', letterSpacing: '.07em' }}>{x.l}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{x.v}</div>
                  </div>
                ))}
              </div>
              <div style={{ border: '1px solid var(--grey-200)', borderRadius: 8, maxHeight: 280, overflowY: 'auto', padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(110px,1fr))', gap: 6 }}>
                {(detail.codes || []).map(c => (
                  <div key={c.code} style={{
                    fontFamily: 'monospace', fontSize: 11, fontWeight: 700,
                    background: c.stdOne ? '#dcfce7' : 'var(--grey-50)',
                    color: c.stdOne ? '#166534' : 'var(--grey-700)',
                    padding: '4px 8px', borderRadius: 4, textAlign: 'center',
                    border: `1px solid ${c.stdOne ? '#86efac' : 'var(--grey-200)'}`,
                  }}>
                    {c.code}
                    {c.stdOne ? <div style={{ fontSize: 9, color: '#166534', marginTop: 1 }}>Used: {c.stdOne + c.stdTwo}</div> : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Create Modal ────────────────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title"><Plus size={18} /> Create Codes Directly</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">School <span className="req">*</span></label>
                  <select className={`form-control ${errors.school_id?'error':''}`}
                    {...register('school_id',{required:'Required'})}>
                    <option value="">Select school…</option>
                    {schools.map(s => <option key={s.id} value={s.id}>{s.school_name} — {s.district_name}</option>)}
                  </select>
                  {errors.school_id && <p className="form-error">{errors.school_id.message}</p>}
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Batch <span className="req">*</span></label>
                    <select className={`form-control ${errors.batch_id?'error':''}`}
                      {...register('batch_id',{required:'Required'})}>
                      <option value="">Select batch…</option>
                      {batchList.map(b => <option key={b.id} value={b.id}>{b.batchName} [{b.batchCode}]</option>)}
                    </select>
                    {errors.batch_id && <p className="form-error">{errors.batch_id.message}</p>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tutorial <span className="req">*</span></label>
                    <select className={`form-control ${errors.tutorial_id?'error':''}`}
                      {...register('tutorial_id',{required:'Required'})}
                      disabled={!watchBatch || !batchTuts.length}>
                      <option value="">{!watchBatch ? 'Select batch first…' : !batchTuts.length ? 'No tutorials in batch' : 'Select tutorial…'}</option>
                      {batchTuts.map(t => <option key={t.id} value={t.id}>{t.name} [{t.key_value}]</option>)}
                    </select>
                    {errors.tutorial_id && <p className="form-error">{errors.tutorial_id.message}</p>}
                  </div>
                </div>
                <div className="form-grid cols-3">
                  <div className="form-group">
                    <label className="form-label">Number of Codes <span className="req">*</span></label>
                    <input type="number" min={1} max={10000}
                      className={`form-control ${errors.quantity?'error':''}`}
                      placeholder="e.g. 200"
                      {...register('quantity',{required:'Required',min:{value:1,message:'Min 1'}})} />
                    {errors.quantity && <p className="form-error">{errors.quantity.message}</p>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Base Price per Code (₹)</label>
                    <input type="number" min={0} step="0.01"
                      className="form-control"
                      placeholder="e.g. 499.00"
                      {...register('base_price')} />
                    <p style={{ fontSize: 11, color: 'var(--grey-400)', marginTop: 3 }}>0 = free / not applicable</p>
                  </div>
                  <div className="form-group" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    {totalValue > 0 && (
                      <div style={{ background: 'var(--orange-50)', border: '1px solid var(--orange-200)', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
                        <div style={{ fontSize: 10, color: 'var(--orange-600)', fontWeight: 600, textTransform: 'uppercase' }}>Total Value</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--orange-700)' }}>₹{totalValue.toFixed(2)}</div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={2} placeholder="Any remarks…" {...register('notes')} />
                </div>

                <div className="alert alert-warning" style={{ fontSize: 12 }}>
                  ⚡ Codes will be generated <strong>immediately</strong> without any approval step.
                  Each code printed on the book sticker can be activated by up to <strong>2 users</strong> (student + parent).
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Tag size={14} /> Create Codes Now</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
